import { skipToken } from "@reduxjs/toolkit/query";
import type { CustomFieldCondition } from "@repo/app-types/entities/filterConditions";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { SearchableMenuPopover } from "@repo/ui/Menu/searchableMenuPopover";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { type FC, useContext, useEffect, useState } from "react";
import {
  ListBox,
  ListBoxItem,
  RootMenuTriggerStateContext,
} from "react-aria-components";
import { useCustomFieldFilterValuesQuery } from "../../api/entities/userCustomField.js";

const SEARCH_DEBOUNCE_MS = 250;

export interface TextCustomFieldConditionMenuPopoverProps {
  customFieldDefinitionId: string;
  customFieldDisplayName: string;
  playbookId?: PlaybookId;
  onSelect: (condition: CustomFieldCondition) => void;
}

export const TextCustomFieldConditionMenuPopover: FC<
  TextCustomFieldConditionMenuPopoverProps
> = (props) => {
  const rootMenuTriggerState = useContext(RootMenuTriggerStateContext);
  const now = useNow();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [search]);

  // Values span the playbook's full history rather than the page's selected
  // date range, so an option stays listed even when the current window holds
  // no calls matching it.
  const trimmedSearch = search.trim();
  const valuesQuery = useCustomFieldFilterValuesQuery(
    props.playbookId
      ? {
          customFieldDefinitionId: props.customFieldDefinitionId,
          playbookId: props.playbookId,
          timeZone: now.zoneName,
          query: trimmedSearch ? debouncedSearch || undefined : undefined,
        }
      : skipToken,
  );
  const values = filterValues(valuesQuery.data?.values ?? [], trimmedSearch);
  const items = values.map((value) => ({ value }));

  const handleSelect = (value: string) => {
    props.onSelect({
      customFieldDefinitionId: props.customFieldDefinitionId,
      operator: "=",
      value,
    });

    rootMenuTriggerState?.close();
  };

  return (
    <SearchableMenuPopover
      className="w-80"
      searchAriaLabel={`Search ${props.customFieldDisplayName} values`}
      searchPlaceholder="Search values..."
      searchMaxLength={255}
      searchValue={search}
      onSearchChange={setSearch}
      isLoading={valuesQuery.isFetching}
      note={
        valuesQuery.data?.hasMore
          ? search
            ? "Keep typing to narrow the results"
            : "Type to search all values"
          : undefined
      }
    >
      <ListBox
        items={items}
        aria-label={`${props.customFieldDisplayName} values`}
        className="outline-hidden"
        renderEmptyState={() => (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            {valuesQuery.isFetching
              ? "Loading values..."
              : valuesQuery.isError
                ? "Values could not be loaded"
                : search
                  ? "No matching values"
                  : "No values available"}
          </div>
        )}
      >
        {(option) => (
          <ListBoxItem
            id={option.value}
            textValue={option.value}
            className={menuStyle().item()}
            onAction={() => handleSelect(option.value)}
          >
            <span className="min-w-0 flex-1 truncate">{option.value}</span>
          </ListBoxItem>
        )}
      </ListBox>
    </SearchableMenuPopover>
  );
};

export function filterValues(
  values: readonly string[],
  search: string,
): string[] {
  if (!search) return [...values];

  const normalizedSearch = search.toLocaleLowerCase();
  return values.filter((value) =>
    value.toLocaleLowerCase().includes(normalizedSearch),
  );
}
